import store from "@/store";
import Vue from "vue";
import EventBus from '../EventBus'
import { 
  REPORT_SCREEN_SHARE_TYPE, 
  ADD_USER_ADDITION_SUFFIX,
  BILIBILI_LIVING_PREVENT_WATCH_SCREENSHARE_TEXT,
  RTC_PRE_AUDIO_RECORD_BLOCK_DURATION,
} from '@/common/constants/rtc'
import {
  isUnverifiedUser,
  getScreenShareDom,
} from './utils'
import { startScreenShare, endScreenShare, updateScreenShare } from '_api/screen-share';
import Agroa from './base'
import { getThemeColor } from "_lib/ThemeColor";
import rtcSdk from './sdk/rtcSdk'
import { generateRandomString } from "_lib/utils/utils";
import { updateMemberRecordState } from "_api/channel";

const MAX_RECEIVE_SCREEN_SHARE_RETRY_COUNT = 5

class AgroaStandard extends Agroa {
  constructor () {
    super()
    this.screen_share_roles = [] // audience & emcee，屏幕共享的身份数组
    this._screenShareUserListOperate = [] // screenShareCallback触发的屏幕共享开关操作列表
    this._awaitScreenShareUser = null // 进入频道后需要展示的屏幕共享用户
    this._screenShareCallbackTriggered = false // 进入房间后screenShareCallback是否首次触发
    this._autoSplitAudioRecordInterval = null // 清除预录的定时器
  }

  screenShareJoinCallback (screen_share_user) {
    this._awaitScreenShareUser = screen_share_user

    // 哔哩哔哩直播过程中不能观看未实名用户的屏幕共享
    if (screen_share_user && store.getters.bilibili_living && isUnverifiedUser(screen_share_user.user_id)) {
      Vue.prototype.$toast.error(BILIBILI_LIVING_PREVENT_WATCH_SCREENSHARE_TEXT)
      return
    }
  }
  screenShareLeaveCallback () {
    this._screenShareCallbackTriggered = false
    this._awaitScreenShareUser = null
  }
  screenShareBindDataCallback () {
    Object.defineProperty(this, 'screen_sharing_info', {
      get: function() {
        return store.getters.screen_sharing_info
      },
    })
    Object.defineProperty(this, 'screen_share_user_list', {
      get: function() {
        return store.getters.screen_share_user_list
      },
    })
  }
  screenShareRegisterEvent () {
    EventBus.$on('rtc-cb:screenShareCallback', this.screenShareCallback.bind(this))
    EventBus.$on('rtc-cb:stopScreenShareCallback', this.stopScreenShareCallback.bind(this))
    EventBus.$on('rtc-cb:retrySubscribeScreen', this.handleRetrySubscribeScreen.bind(this))
  }

  /** 屏幕共享功能 */
  //  处理屏幕共享，区分electron和web逻辑
  handleScreenShare() {
    if (!rtcSdk.RtcApi) return
    if (process.env.IS_CLIENT) {
      EventBus.$dynamic('ScreenShareConfig')
    } else {
      this.startScreenCapture(this.cur_channel_data)
    }
  }
  // 获取屏幕列表(仅electron api需要)
  async getScreenCaptureSources() {
    return rtcSdk.getScreenCaptureSources()
  }
  // 开始屏幕共享
  async startScreenCapture({channel_id, room_id}, data, params = {}, isEdit) {
    if (!rtcSdk.RtcApi || this._startScreenCaptureLock) return
    let res = await this.reportScreenShareRole(isEdit ? REPORT_SCREEN_SHARE_TYPE.EDIT : REPORT_SCREEN_SHARE_TYPE.START, {
      role: 'emcee',
      resolution: params.resolution,
      frame_rate: params.frame_rate,
      sub_user_id: 0,
      channel_id, 
      room_id,
    })
    if (res?.data.status !== 'ok') return
    params.screen_share_protocol = this.setting_data.screen_share_protocol,
    params.screen_share_hardware_encode = this.setting_data.screen_share_hardware_encode,

    clientLog('rtc-start-screen-capture')

    this._startScreenCaptureLock = true
    if (store.getters.is_phone_mic_connecting) { // 开启屏幕共享需要关闭手机麦克风
      EventBus.$emit('Phone_Mic_Disconnect')
    }
    if (process.env.IS_CLIENT) { // 客户端需要切换到electron版本进行屏幕共享
      await this.changeBgmState(false) // 伴奏和屏幕共享的rtc id不一样，需要先退出伴奏
      EventBus.$emit('Clear_System_Bgm_state') // 手机麦克风和伴奏功能冲突
    }
    const dom = document.querySelector(`#screen-block-me-${channel_id}`)
    try { // websdk共享时在web中点击取消会导致startScreenCapture报错
      let code = await rtcSdk.RtcApi.startScreenCapture(dom, data, params)
      if (code === 'window_path_error') { // 屏幕共享选择窗口时，获取不到窗口的路径(没有管理员权限)
        Vue.prototype.$query({
          config: {
            title: '已为你关闭共享声音，请以管理员身份运行黑盒语音后重试',
            desc: '因没有系统权限，共享当前应用时会共享系统声音，以管理员身份运行后可正常共享应用声音',
            confirm: {
              text: '我知道了'
            }
          }
        })
        this.setScreenCaptureVolume(0)
      } else {
        this.setScreenCaptureVolume(params.share_window_audio_mute ? 0 : 100)
      }
      
      let user_id = String(this.user_info.user_id)
      if (this.screen_sharing_info.user_id && !this.screen_sharing_info.is_me) { // 如果正在观看别人的屏幕共享，需要先上报结束观看
        await this.reportScreenShareRole(REPORT_SCREEN_SHARE_TYPE.STOP, {
          role: 'audience',
          channel_id, 
          room_id,
        })
        await rtcSdk.RtcApi.stopReceiveScreenCapture(this.screen_sharing_info.user_id)
      }
      // 将自己加到分享用户列表中
      store.dispatch('SET_SCREEN_SHARE_USER_LIST', {
        type: 'add',
        data: {
          user_id,
          channel_id,
          is_me: true,
        }
      })

      // 当正在展示屏幕共享 或者是主持人模式
      let dispatchFlag = this.screen_sharing_info.user_id || this.clientData.host_mode;

      if (dispatchFlag) { 
        store.dispatch('SET_SCREEN_SHARING_INFO', {
          user_id,
          channel_id,
          screen_share_info: {
            resolution: params.resolution,
            frame_rate: params.frame_rate,
          },
          is_me: true,
        })
      }

      // 设置自己正在屏幕共享状态
      store.commit('SET_MY_SCREEN_SHARING', {
        resolution: params.resolution,
        frame_rate: params.frame_rate,
      })
      EventBus.$emit('Add_Room_State_Number', this.cur_channel_data.room_id, 'screen', 1);
    } catch (e) {
      console.error(e) 
      // 如果报错需要再上报后端停止屏幕共享
      await this.reportScreenShareRole(REPORT_SCREEN_SHARE_TYPE.STOP, {
        role: 'emcee',
        channel_id, 
        room_id,
      })
    }
    this._startScreenCaptureLock = false
  }
  // 结束屏幕共享
  async stopScreenCapture() {
    if (this._stopScreenCaptureLock) return
    clientLog('rtc-stop-screen-capture')
    this._stopScreenCaptureLock = true
    await rtcSdk.RtcApi.stopScreenCapture()
    store.dispatch('SET_SCREEN_SHARE_USER_LIST', {
      type: 'remove',
      data: {
        user_id: String(this.user_info.user_id),
      }
    })
    store.commit('SET_MY_SCREEN_SHARING', false)
    this.reportScreenShareRole(REPORT_SCREEN_SHARE_TYPE.STOP, {
      role: 'emcee',
      room_id: this.clientData.room_id,
      channel_id: this.clientData.channel_id,
    })
    // 如果当前正在看屏幕共享，更新展示的屏幕共享，默认展示观众最多屏幕共享的用户
    if (this.screen_sharing_info.user_id) {
      await this.checkSharingInfo()
      await this.checkShowScreenShare()
    }
    this._stopScreenCaptureLock = false
    EventBus.$emit('Add_Room_State_Number', this.cur_channel_data.room_id, 'screen', 0);
  }
    // 切换房间后刷新屏幕共享，重新展示屏幕共享的内容
  async refreshShowScreenShare() {
    let {user_id, is_me} = this.screen_sharing_info // 如果有user_id说明离开离开房间时正在观看屏幕共享并且没有退出语音频道
    let {channel_id} = this.cur_channel_data
    if (user_id) {
      if (store.getters.my_screen_sharing && is_me) {
        const dom = document.querySelector(`#screen-block-me-${channel_id}`)
        dom && await rtcSdk.RtcApi.refreshScreenCapture(dom)
      } else if (!is_me) { // 如果正在观看的不是自己，需要重新拉取展示观看的屏幕共享用户的流
        await this.checkSharingInfo()
        await this.checkShowScreenShare()
      }
    }
  }
  // 刷新个人的屏幕共享预览，在预览组件创建 & 聚焦展示时刷新
  async refreshPreviewScreenCapture() {
    if (store.getters.my_screen_sharing) {
      let {channel_id} = this.cur_channel_data
      const dom = document.querySelector(`#screen-block-me-${channel_id}`)
      dom && await rtcSdk.RtcApi.refreshScreenCapture(dom)
    } 
  }
  // 停止本地屏幕共享预览
  async stopPreviewScreenCapture() {
    await rtcSdk.RtcApi.stopPreviewScreenCapture()
  }
  // 切换屏幕共享窗口的用户
  async switchScreenShareUser(user_info) { 
    if (user_info.user_id == this.screen_sharing_info.user_id) return
    // 哔哩哔哩直播过程中不能观看未实名用户的屏幕共享
    if (store.getters.bilibili_living && isUnverifiedUser(user_info.user_id)) {
      Vue.prototype.$toast.error(BILIBILI_LIVING_PREVENT_WATCH_SCREENSHARE_TEXT)
      return
    }
    clientLog('rtc-switch-screen-share')
    if (this.screen_sharing_info.user_id) {
      await this.reportScreenShareRole(REPORT_SCREEN_SHARE_TYPE.STOP, { // 上报取消上一次的观看状态
        role: 'audience',
        room_id: this.clientData.room_id,
        channel_id: this.clientData.channel_id,
      })
      await rtcSdk.RtcApi.stopReceiveScreenCapture(this.screen_sharing_info.user_id)
    }
    let screen_user_info = this.screen_share_user_list.find(v => v.user_id == user_info.user_id)
    if (screen_user_info) {
      store.dispatch('SET_SCREEN_SHARING_INFO', await this.audienceReportCheck(screen_user_info)) // 上报并切换为新的观众状态
      Vue.nextTick(() => { // 需要nextTick等待`#screen-block-${channel_id}`屏幕共享的展示dom创建
        this.checkShowScreenShare()
      })
    }
  }
  // 停止观看屏幕共享
  async stopWatchScreenShare(clearSharingInfo = true) {
    clientLog('rtc-stop-receive-screen-share')
    let id = this.screen_sharing_info.user_id
    if (clearSharingInfo) {
      store.dispatch('SET_SCREEN_SHARING_INFO', {})
    }
    rtcSdk.RtcApi.stopReceiveScreenCapture(id)
    await this.reportScreenShareRole(REPORT_SCREEN_SHARE_TYPE.STOP, {
      role: 'audience',
      room_id: this.clientData.room_id,
      channel_id: this.clientData.channel_id,
    })
  }
  clearScreenShare() { // 清除屏幕共享状态
    store.dispatch('SET_SCREEN_SHARE_USER_LIST', [])
    store.dispatch('SET_SCREEN_SHARING_INFO', {})
    if (store.getters.my_screen_sharing) {
      store.commit('SET_MY_SCREEN_SHARING', false)
    }
    this.reportScreenShareRole(REPORT_SCREEN_SHARE_TYPE.STOP, {
      role: this.screen_share_roles,
      room_id: this.clientData.room_id,
      channel_id: this.clientData.channel_id,
    })
  }
  // 检查screen_sharing_info是否在分享屏幕的用户列表中并更新screen_sharing_info
  async checkSharingInfo(active = false) {
    let screenShareUser = this.screen_sharing_info
    if (!this.screen_share_user_list.find(v => v.user_id == screenShareUser.user_id)) { // 如果当前的screen_sharing_info不在screen_share_user_list分享中列表里，需要重置为最后一个用户或者清空
      if (screenShareUser.user_id) {
        await this.reportScreenShareRole(REPORT_SCREEN_SHARE_TYPE.STOP, { // 上报退出正在观看的屏幕共享
          role: 'audience',
          room_id: this.clientData.room_id,
          channel_id: this.clientData.channel_id,
        })
      }

      let screen_share_user_list
      if (store.getters.bilibili_living) {
        screen_share_user_list = this.screen_share_user_list.filter((user) => user.verified)
      } else {
        screen_share_user_list = this.screen_share_user_list
      }

      // 如果有人正在共享，并且设置了自动观看或者用户手动操作观看，才赋值screenShareUser
      if (screen_share_user_list.length > 0 
        && (store.getters.user_account_settings.auto_receive_screenshare_v2 || active)) {
        screen_share_user_list.sort((a, b) => {
          let a_count = 0, b_count = 0
          if (a.screen_share_info) {
            let {audience_count, audience_limit} = a.screen_share_info
            a_count = audience_count < audience_limit ? audience_count : 0
          }
          if (b.screen_share_info) {
            let {audience_count, audience_limit} = b.screen_share_info
            b_count = audience_count < audience_limit ? audience_count : 0
          }
          return b_count - a_count
        })
        screenShareUser = screen_share_user_list[0]
        if (screenShareUser.user_id !== this.user_info.user_id) { // 观看其他人的屏幕共享状态上报
          screenShareUser = await this.audienceReportCheck(screenShareUser)
        }
      } else {
        screenShareUser = {}
      }
    }
    store.dispatch('SET_SCREEN_SHARING_INFO', screenShareUser)
  }
  async checkShowScreenShare() { // 校验是否需要展示屏幕共享窗口
    let { channel_id, room_id } = this.cur_channel_data
    if (!channel_id) {
      await rtcSdk.RtcApi.stopReceiveScreenCapture(this.screen_sharing_info.user_id)
      await rtcSdk.RtcApi.stopScreenCapture()
      return
    }
    let { user_id, is_me, is_audience_full, screen_share_info } = this.screen_sharing_info
    if (this.screen_share_user_list.length > 0 && user_id) {
      if (is_me) {
        await rtcSdk.RtcApi.stopReceiveScreenCapture(user_id) // 如果切换到自己的流，需要暂停接收的屏幕流
        return // 不需要进行其他操作，如果is_me会直接展示预览自己屏幕共享的dom，隐藏他人屏幕共享的dom
      }
      this.handleReceiveEmceeScreenShare(this.cur_channel_data, this.screen_sharing_info)
    } else {
      await rtcSdk.RtcApi?.stopReceiveScreenCapture(user_id)
    }
  }
  async screenShareCallback(user_id, available) { // 其他用户的屏幕共享状态改变回调
    // 当前用户自身的开关屏幕共享操作不需要处理，会在开启关闭屏幕共享的函数中处理
    if (user_id == this.user_info.user_id) return

    console.log('screenShareCallback', user_id, available)
    let {channel_id, room_id} = this.clientData
    if (this.screen_share_user_list.length === 0) { // 触发回调时如果共享用户列表为空，重置is_allow_show_screen_share_button字段
      store.commit('SET_IS_ALLOW_SHOW_SCREEN_SHARE_BUTTON', false)
    }
    if (available) {
      this._screenShareUserListOperate.push({
        type: 'add',
        data: {
          user_id,
          channel_id,
        }
      })
    } else {
      this._screenShareUserListOperate.push({
        type: 'remove',
        data: {
          user_id,
        }
      })
      // 其他人关闭屏幕共享也需要重置其主播模式的ignore标记，目前只有v2模式的trtc需要
      let { anchor_mute_user_ids} = store.getters
      let _anchor_mute_user_ids = JSON.parse(JSON.stringify(anchor_mute_user_ids))
      let user = _anchor_mute_user_ids[ADD_USER_ADDITION_SUFFIX(user_id)]
      if (user) {
        user.ignore = false
        store.commit('SET_ANCHOR_MUTE_USER_IDS', _anchor_mute_user_ids)
      }
    }

    if (this._screenShareUserListOperate.length > 0) {
      let cb = async () => {
        clearTimeout(this._screenShareCallbackTimeout)
        this._screenShareCallbackTimeout = null
        console.log('this._screenShareUserListOperate', this._screenShareUserListOperate)
        // _screenShareUserListOperate在此处统一进行数据更新，防止提早更新导致list有值显示屏幕共享按钮，在后续展示屏幕共享dom时会闪烁
        this._screenShareUserListOperate.forEach(obj => {
          store.dispatch('SET_SCREEN_SHARE_USER_LIST', obj)
        })
        // 如果用户手动关闭了屏幕共享观看就不再自动展示屏幕共享了
        // 如果存在screen_sharing_info.user_id说明是双击正在屏幕共享用户进入频道，需要展示对应用户的屏幕共享
        this._screenShareUserListOperate = []
        if (this._awaitScreenShareUser || 
          (!store.getters.is_user_stop_receive_screen_share)) {
          if (this._awaitScreenShareUser) {
            store.dispatch('SET_SCREEN_SHARING_INFO', await this.audienceReportCheck(this._awaitScreenShareUser)) // 上报并切换为新的观众状态
            this._awaitScreenShareUser = null
          }
          
          await this.checkSharingInfo()
          await this.checkShowScreenShare()
        }
        store.commit('SET_IS_ALLOW_SHOW_SCREEN_SHARE_BUTTON', true)
      }

      // 如果_screenShareCallbackTimeout存在，不需要再调用cd，等cb触发时会取到最新的_screenShareUserListOperate
      if (!this._screenShareCallbackTimeout) {
        if (!this._screenShareCallbackTriggered) {
          // 刚进入频道时如果有多人进行屏幕共享会同时触发多次，因此进行延迟处理
          this._screenShareCallbackTriggered = true
          this._screenShareCallbackTimeout = setTimeout(cb, 400)
        } else {
          // 后续如果还使用延迟，在用户切换屏幕共享时，可能会由于延迟导致结束和开启屏幕共享触发同一次cb函数
          // 如果用户没开启自动观看屏幕共享，会由于共享列表的用户没有变化导致不会自动关闭观看屏幕共享
          cb()
        }
      }

    }

  }
  stopScreenShareCallback() { // 被动停止屏幕共享回调
    this.stopScreenCapture()
  }
  // 校验是否允许进行屏幕共享观看并处理返回结果，如果不允许或者接口报错返回{}，如果用户满人对象拼接isAudienceFull: true
  async audienceReportCheck(user_info) {
    let {channel_id, room_id} = this.clientData
    if (!user_info.is_me) {
      try {
        let res = await this.reportScreenShareRole(REPORT_SCREEN_SHARE_TYPE.START, {
          role: 'audience',
          sub_user_id: parseInt(user_info.user_id),
          channel_id, 
          room_id,
        })
        if (res.data.status !== 'ok') {
          return {}
        } else {
          if (res.data.result.state == 1) { // state = 1 观众满人
            return {
              ...user_info,
              is_audience_full: true
            }
          } 
        }
      } catch (e) {
        return {}
      }
    }
    return user_info
  }
  async reportScreenShareRole(type, params) { // 上报屏幕共享 主播 & 观众状态
    if (!params.role || (Array.isArray(params.role) && params.role.length === 0)) return
    
    let axios = !type 
      ? endScreenShare 
      : type === REPORT_SCREEN_SHARE_TYPE.START 
        ? startScreenShare
        : updateScreenShare
    let avatar_main_color = store.getters.avatar_main_color
    if (type) {
      !this.screen_share_roles.includes(params.role) && this.screen_share_roles.push(params.role)

      // 如果没有头像主色，计算头像主色并更新vuex，
      // 在调用startScreenShare和updateScreenShare时，后端会根据main_color字段更新account接口中的avatar_main_color
      if (!avatar_main_color) {
        avatar_main_color = await this.getAvatarThemeColor()
        store.commit('SET_AVATAR_MAIN_COLOR', avatar_main_color)
      }
      params.main_color = avatar_main_color
    } else {
      params.role = !Array.isArray(params.role) ? [params.role] : params.role
      this.screen_share_roles = this.screen_share_roles.filter(v => !params.role.includes(v))
    }
    params.ws_id = store.getters.ws_id
    return axios({}, params).catch(err => {
      console.error(err)
      Vue.prototype.$toast.error('请求错误，请刷新重试')
    }).finally(() => {
      clientLog('rtc-screen-share-report', params)
    })
  }
  // 设置屏幕共享的上传流声音大小
  setScreenCaptureVolume(level) {
    // 只有electron支持屏幕共享上传流声音调整
    rtcSdk.RtcApi.setScreenCaptureVolume && rtcSdk.RtcApi.setScreenCaptureVolume(level)
    store.commit('SET_SCREEN_SHARING_VOICE_LEVEL', level)
  }
  async getAvatarThemeColor() {
    let bgColor = ''
    try {
      bgColor = await getThemeColor(
        store.getters.user_info.avatar,
        { sMin: 0, sMax: 80, vMin: 20, vMax: 100, type: 1 },
        false
      );
    } catch (e) {
      console.error(e)
    }
    return bgColor
  }

  /**
   * 开始录音
   * @description 只有用户在频道内时才允许录音，需要通过房间和频道的信息进行初始化，拼接audio_record_path变成对应的文件名
   * @param {*} info 房间和频道的信息
   * @returns 
   */
  async createVoiceRecord(info) {
    clientLog('rtc-start-record', info)
    let { 
      setting_data: { audio_record_path = "", audio_record_list = [] } = {}, 
      is_recording, 
      user_heybox_id,
      is_pre_recording,
    } = store.getters
    if(is_recording) {
      return false
    }
    if(!audio_record_path) {
      const config = {
        title: '请先设置录音保存路径',
        desc: '',
        icon: 'icon-toast-warn',
        iconColor: 'yellow',
        confirm: '前往设置',
        confirmCB: () => {
          EventBus.$dynamic('UserConfig', 'record')
        }
      }
      EventBus.$emit('Show_Record_Operation_Tip', true, config)
      return false
    }
    audio_record_path = audio_record_path.replace(/\\/g, '\\\\')
    const { channel_name, room_name, room_id, channel_id } = info
    const time = new Date()
    
    const EXT = 'wav'
    const name = `${room_name}-${channel_name}-${time.getTime()}的录音.${EXT}`
    const filePath = `${audio_record_path.replace(/\\\\/g, '\\')}\\${name}`
    
    let res
    if(is_pre_recording) {
      if(this._autoSplitAudioRecordInterval) {
        clearInterval(this._autoSplitAudioRecordInterval)
        this._autoSplitAudioRecordInterval = null
      }
      res = true
    } else {
      const { add } = await window.electronAPI.checkFolderAuthority(audio_record_path)
      if(!add) {
        Vue.prototype.$toast.error('没有权限创建文件，请检查文件路径')
        return false
      }
      res = rtcSdk.startRecord(filePath)
    }
    clientLog('rtc-start-record-res', res)
    let id = '';
    if(res) {
      id = generateRandomString(16);
      const createObj = {
        id,
        name,
        filePath,
        create_time: time.getTime(),
        duration: undefined,
        size: undefined,
        user_id: user_heybox_id,
      }
      audio_record_list.unshift(createObj)
      store.commit('SET_RECORDING_ID', createObj.id);
      store.dispatch('SET_SETTING_DATA_PARAM', {
        audio_record_list
      });
      updateMemberRecordState({
        channel_id,
        room_id,
        is_start: true,
      });
    }
    return id
  }

  // 停止录音
  async closeVoiceRecord() {
    clientLog('rtc-stop-record')
    const { recording_id } = store.getters
    if(!recording_id) {
      return false
    }
    const res = rtcSdk.stopRecord()
    if(res) {
      EventBus.$emit('Update_Recording_Data')
      const { recording_id: finishId, setting_data: { audio_record_list = [] } = {}, pre_recording_block_queue } = store.getters
      store.commit('SET_RECORDING_ID', '')
      updateMemberRecordState({
        channel_id: store.getters.cur_channel_data.channel_id,
        room_id: store.getters.cur_channel_data.room_id,
        is_start: false,
      })
      const config = {
        title: '录音文件已生成',
        desc: '',
        icon: 'icon-toast-success',
        iconColor: 'green',
        confirm: '查看文件',
        confirmCB: () => {
          EventBus.$dynamic('UserConfig', 'record')
        }
      }
      EventBus.$emit('Show_Record_Operation_Tip', true, config)
      if(store.getters.pre_recording_block_queue.length) {
        const item = audio_record_list.find(item => item.id === finishId)
        if(item) {
          // 将所有的预录制视频进行合并 然后变成一个文件放到当前的item的filePath中
          const { filePath, id } = item
          store.dispatch('SET_AUDIO_CONCAT_ID', id)
          const concatRes = await (window.electronAPI && window.electronAPI.concatAudioFile({
            audioFiles: pre_recording_block_queue, 
            outputPath: filePath,
          })).catch((_err) => {
            store.dispatch('SET_AUDIO_CONCAT_ID', '')
          })
          store.dispatch('SET_AUDIO_CONCAT_ID', '')
        }
        store.dispatch('CLEAR_PRE_RECORD_BLOCK')
        this.startPreAudioRecordInterval()
      }
    }
    return res
  }

  /**
   * 开始预录制
   */
  startPreAudioRecordInterval() {
    const channel_data = store.getters.cur_channel_data
    const permission = Vue.prototype.$permission.judge('AudioRecord', store.getters.cur_channel_data)
    let isMicMute = false
    if(channel_data.channel_id) {
      isMicMute = Boolean(channel_data.mute || channel_data.mute_room)
    }
    const limitations = store.getters.limitations
    // 如果没有使用预录、正在录制、正在预录制、被静音以及收到限制 则直接提前返回，不做任何操作
    if(!permission || !store.getters.setting_data.pre_record_switch || store.getters.is_recording || this._autoSplitAudioRecordInterval || isMicMute || limitations?.tooltip) {
      return
    }

    const func = async () => {
      const stopLastRes = rtcSdk.stopRecord()
      let { 
        setting_data: { audio_record_path = "" } = {}, 
      } = store.getters
      const { add } = await window.electronAPI.checkFolderAuthority(audio_record_path)
      if(!add && !audio_record_path && !stopLastRes) {
        this.stopPreAudioRecordInterval(true)
        return
      }
      audio_record_path = audio_record_path.replace(/\\/g, '\\\\')
      const time = new Date()
      
      const EXT = 'wav'
      const name = `preload_${time.getTime()}.${EXT}`
      const filePath = `${audio_record_path.replace(/\\\\/g, '\\')}\\${name}`

      const res = rtcSdk.startRecord(filePath)
      if(res) {
        store.dispatch('ADD_PRE_RECORD_BLOCK', filePath)
      } else {
        this.stopPreAudioRecordInterval(true)
      }
    }

    func()
    this._autoSplitAudioRecordInterval = setInterval(func, RTC_PRE_AUDIO_RECORD_BLOCK_DURATION)
  }

  /**
   * 停止预录制
   * @param {Boolean} isClosed 是否被关闭 如果是被关闭 则清空所有预录制文件， 如果不是，则直接返回
   * @returns 
   */
  stopPreAudioRecordInterval(isClosed = false) {
    // 处理相关文件
    return new Promise((resolve) => {
      if(this._autoSplitAudioRecordInterval) {
        clearInterval(this._autoSplitAudioRecordInterval)
        this._autoSplitAudioRecordInterval = null
      }
      if(isClosed) {
        rtcSdk.stopRecord()
        this._clearPreRecordFileTimeOut = setTimeout(() => {
          store.dispatch('CLEAR_PRE_RECORD_BLOCK')
          clearTimeout(this._clearPreRecordFileTimeOut)
          this._clearPreRecordFileTimeOut = null
        }, 1000)
      }
      resolve()
    })
  }

  /**
   * 处理接收屏幕共享订阅
   * @param {*} channel_data 
   * @param {*} screen_share_info 
   */
  handleReceiveEmceeScreenShare(channel_data, screen_share_info, retryCount = 0) {
    const { user_id, is_audience_full } = screen_share_info

    /** 
     * 判断一下是否还是当前观看的屏幕共享
     * 是否是同一个频道 && 是否是同一个用户
     * */ 
    const checkIsSameScreenShare = (channel_data, screen_share_info) => {
      return (
        channel_data.channel_id == store.getters.cur_channel_data.channel_id &&
        screen_share_info.user_id == store.getters.screen_sharing_info.user_id
      );
    };

    const dom = getScreenShareDom(channel_data)
    if (dom && !is_audience_full) {
      clientLog('rtc-start-receive-screen-share')
      rtcSdk.RtcApi.startReceiveScreenCapture(user_id, dom).catch((_e) => {
        if(retryCount < MAX_RECEIVE_SCREEN_SHARE_RETRY_COUNT) {
          this._receiveScreenCaptureTimeout = setTimeout(async () => {
            if(!checkIsSameScreenShare(channel_data, screen_share_info)) {
              return 
            }
            await rtcSdk.RtcApi?.stopReceiveScreenCapture(user_id)
            this.handleReceiveEmceeScreenShare(channel_data, screen_share_info, retryCount + 1)

            // 清空定时器
            clearTimeout(this._receiveScreenCaptureTimeout)
            this._receiveScreenCaptureTimeout = null
          }, 1000)
        } else {
          if(!checkIsSameScreenShare(channel_data, screen_share_info)) {
            return
          }
          Vue.prototype.$toast.error('屏幕共享拉流失败')
        }
      })
    } else {
      rtcSdk.RtcApi.stopReceiveScreenCapture(user_id)
    }
  }
  /**
   * 尝试重新拉流屏幕共享
   * @description 如果拉流失败，则尝试重新拉流
   * @returns 
   */
  async handleRetrySubscribeScreen(user_id) {
    clientLog('[rtc standard] handleRetrySubscribeScreen', user_id)
    // 如果是当前屏幕共享出问题，则提示用户重新拉流
    if(user_id == this.screen_sharing_info.user_id) {
      Vue.prototype.$query({
        config: {
          title: '屏幕共享拉流失败',
          confirm: {
            text: '重试'
          },
          cancel: {
            text: '取消'
          }
        },
        cancelCB: async () => {
          await this.stopWatchScreenShare()
        },
        confirmCB: async () => {
          const originScreenSharingInfo = this.screen_sharing_info
          await this.stopWatchScreenShare()
          await this.switchScreenShareUser(originScreenSharingInfo)
        }
      })
    }
  }
}

export default AgroaStandard